import { useState } from 'react';
import { useCreateDeclaration, useAllTeamUsers, usePositionsList } from '@/api/supervisor';
import { Combobox } from '@/components/shared/combobox';
import { X } from 'lucide-react';

// Diálogo modal para crear una declaración de competencia. Auto-contenido.
// Extraído de declarations.tsx por ENG-LOC-01.
export function CreateDeclarationDialog({ onClose }: { onClose: () => void }) {
  const [userId, setUserId] = useState<number>(0);
  const [positionId, setPositionId] = useState<number>(0);
  const [declarationType, setDeclarationType] = useState('initial');

  const { data: teamData, isLoading: loadingTeam } = useAllTeamUsers();
  const { data: positionsRes, isLoading: loadingPositions } = usePositionsList();
  const createMutation = useCreateDeclaration();

  const teamUsers = teamData?.data ?? [];
  const positions = positionsRes?.data ?? [];

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!userId || !positionId) return;

    createMutation.mutate(
      {
        user_id: userId,
        position_id: positionId,
        declaration_type: declarationType,
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Crear Declaracion</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User select */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Empleado</label>
            {loadingTeam ? (
              <p className="text-sm text-muted-foreground">Cargando...</p>
            ) : (
              <Combobox
                value={userId ? String(userId) : ''}
                onChange={(v) => setUserId(v ? Number(v) : 0)}
                options={teamUsers.map((u) => ({
                  value: String(u.id),
                  label: `${u.first_name} ${u.last_name}`,
                }))}
                placeholder="Seleccionar empleado..."
                clearable
              />
            )}
          </div>

          {/* Position select */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Posición</label>
            {loadingPositions ? (
              <p className="text-sm text-muted-foreground">Cargando...</p>
            ) : (
              <Combobox
                value={positionId ? String(positionId) : ''}
                onChange={(v) => setPositionId(v ? Number(v) : 0)}
                options={positions.map((p) => ({
                  value: String(p.id),
                  label: p.name,
                  sublabel: p.code ?? undefined,
                }))}
                placeholder="Seleccionar posición..."
                clearable
              />
            )}
          </div>

          {/* Type select */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Tipo</label>
            <select
              value={declarationType}
              onChange={(e) => setDeclarationType(e.target.value)}
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground"
            >
              <option value="initial">Inicial</option>
              <option value="maintenance">Mantenimiento</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !userId || !positionId}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creando...' : 'Crear Declaracion'}
            </button>
          </div>

          {createMutation.isError && (
            <p className="text-sm text-destructive">Error al crear la declaracion.</p>
          )}
        </form>
      </div>
    </div>
  );
}
